import { useEffect, useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Download, Search, Mail, Phone, CheckCircle2, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { api, API_URL } from "@/lib/api";

export default function ContactsAdmin() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/admin/contacts").then((r) => setRows(r.data)); }, []);

  const resendDownload = useCallback(async (email, force = false) => {
    if (!email) return;
    if (!window.confirm(`Send the CSN download link to ${email}?`)) return;
    try {
      const r = await api.post("/admin/contacts/resend-download", { email, force });
      if (r.data.ok) {
        toast.success(`Link sent · email:${r.data.sent_email ? "✓" : "✗"} sms:${r.data.sent_sms ? "✓" : "✗"}`);
      } else {
        toast.error("Failed to deliver — check backend logs.");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed";
      if (msg.includes("No paid purchase")) {
        if (window.confirm("This email has no confirmed payment. Send anyway (force)?")) {
          return resendDownload(email, true);
        }
      } else {
        toast.error(msg);
      }
    }
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const qq = q.toLowerCase().trim();
    return rows.filter((r) => {
      if (filter === "paid" && !r.paid) return false;
      if (filter === "presave" && r.source !== "presave") return false;
      if (filter === "purchase" && r.source !== "purchase") return false;
      if (filter === "call" && r.source !== "call") return false;
      if (filter === "sms" && r.source !== "sms") return false;
      if (!qq) return true;
      return [r.name, r.email, r.phone].some((v) => (v || "").toLowerCase().includes(qq));
    });
  }, [rows, q, filter]);

  return (
    <div data-testid="contacts-admin">
      <div className="label-mono mb-2">/ Contacts</div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">Contacts</h1>
          <p className="text-zinc-500 text-sm mt-2">Pre-saves + purchases captured from release landings.</p>
        </div>
        <a href={`${API_URL}/admin/contacts.csv`} download
           className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-black font-bold text-sm bg-cyan-400 hover:scale-[1.02] transition-transform"
           data-testid="contacts-export-csv">
          <Download size={14}/> EXPORT CSV
        </a>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"/>
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone…"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-[#121215] border border-white/10 text-sm focus:border-cyan-500/40 outline-none"
            data-testid="contacts-search"
          />
        </div>
        <div className="flex gap-1 p-1 rounded-lg bg-[#121215] border border-white/5">
          {["all", "presave", "purchase", "call", "sms", "paid"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
                    className={`px-3 py-1.5 rounded-md text-xs font-bold tracking-wider transition ${filter === f ? "bg-cyan-500/15 text-cyan-300" : "text-zinc-500 hover:text-zinc-300"}`}
                    data-testid={`contacts-filter-${f}`}>
              {f.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-white/5 bg-[#121215] overflow-hidden">
        {!rows ? (
          <div className="p-12 text-center text-zinc-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No contacts match.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="contacts-table">
              <thead>
                <tr className="text-[10px] tracking-widest text-zinc-500 border-b border-white/5">
                  <th className="text-left px-5 py-3 font-mono">When</th>
                  <th className="text-left px-5 py-3 font-mono">Source</th>
                  <th className="text-left px-5 py-3 font-mono">Name</th>
                  <th className="text-left px-5 py-3 font-mono">Email</th>
                  <th className="text-left px-5 py-3 font-mono">Phone</th>
                  <th className="text-left px-5 py-3 font-mono">Delivery</th>
                  <th className="text-left px-5 py-3 font-mono">Status</th>
                  <th className="text-right px-5 py-3 font-mono">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <motion.tr
                    key={r.id || i}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.4) }}
                    className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-3 text-zinc-400 font-mono text-xs whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold tracking-widest ${
                        r.source === "purchase" ? "bg-emerald-500/15 text-emerald-300" :
                        r.source === "call"     ? "bg-purple-500/15 text-purple-300" :
                        r.source === "sms"      ? "bg-amber-500/15 text-amber-300" :
                        "bg-cyan-500/15 text-cyan-300"
                      }`}>
                        {(r.source || "").toUpperCase()}
                      </span>
                      {r.release_slug && <span className="ml-2 text-[10px] text-zinc-500 font-mono">/{r.release_slug}</span>}
                    </td>
                    <td className="px-5 py-3">{r.name || <span className="text-zinc-600">—</span>}</td>
                    <td className="px-5 py-3">
                      <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 text-cyan-300 hover:underline">
                        <Mail size={12}/> {r.email}
                      </a>
                    </td>
                    <td className="px-5 py-3">
                      {r.phone ? (
                        <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-white">
                          <Phone size={12}/> {r.phone}
                        </a>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-5 py-3 text-zinc-400 font-mono text-xs">{r.delivery?.toUpperCase()}</td>
                    <td className="px-5 py-3">
                      {r.paid ? (
                        <span className="inline-flex items-center gap-1.5 text-emerald-300 text-xs font-bold">
                          <CheckCircle2 size={12}/> PAID
                        </span>
                      ) : r.source === "purchase" ? (
                        <span className="inline-flex items-center gap-1.5 text-amber-300 text-xs font-bold">
                          <Clock size={12}/> PENDING
                        </span>
                      ) : (
                        <span className="text-zinc-600 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {r.source === "purchase" && r.email && (
                        <button
                          type="button"
                          onClick={() => resendDownload(r.email)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 text-[10px] font-bold tracking-widest transition"
                          data-testid={`contacts-resend-${r.id || r.email}`}
                          title="Re-mint and send the album download link"
                        >
                          <Send size={11}/> SEND LINK
                        </button>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rows && (
        <div className="text-zinc-500 text-xs mt-4 font-mono tracking-wider">
          {filtered.length} OF {rows.length} CONTACTS
        </div>
      )}
    </div>
  );
}
